import './index.css';
import {
    confirmSignIn,
    fetchAuthSession,
    fetchUserAttributes,
    getCurrentUser
} from 'aws-amplify/auth';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { selectTemporaryUser } from '../../app/userSlice';
import { FormHeader } from '../../components/formHeader';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchUserDataSuccess } from '../../app/authSlice';
import { toSerializableAuthUser } from '../../utils/user';
import type { CognitoUser } from '../../interfaces/cognitoUser';

export function SetPassword(): React.ReactNode {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { register, handleSubmit } = useForm<{
        password: string;
        repeatPassword: string;
    }>();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const email = queryParams.get('email');

    const [passwordShown, setPasswordShown] = useState<boolean>(false);
    const [repeatPasswordShown, setRepeatPasswordShown] =
        useState<boolean>(false);
    const [error, setError] = useState<string>();

    const user = useAppSelector(selectTemporaryUser);

    const handleSetPassword = useCallback(
        async (data: { password: string; repeatPassword: string }) => {
            try {
                if (data.password === '') {
                    setError('Passwords should not be empty!');
                } else if (data.password !== data.repeatPassword) {
                    setError('Passwords must match!');
                } else {
                    confirmSignIn({ challengeResponse: data.password })
                        .then(async (response) => {
                            // Fetch the updated user data after password change
                            if (response.isSignedIn) {
                                const session = await fetchAuthSession();
                                const currentUser = await getCurrentUser();
                                const attributes = await fetchUserAttributes();

                                // Transform to match expected CognitoUser structure
                                const cognitoUser: CognitoUser = {
                                    username: currentUser.username,
                                    userId: currentUser.userId,
                                    signInDetails: currentUser.signInDetails,
                                    challengeName: '',
                                    attributes: {
                                        email:
                                            attributes.email ??
                                            currentUser.username
                                    },
                                    signInUserSession: {
                                        accessToken: {
                                            payload:
                                                session.tokens?.accessToken
                                                    ?.payload ?? {}
                                        }
                                    }
                                };

                                dispatch(
                                    fetchUserDataSuccess(
                                        toSerializableAuthUser(cognitoUser)
                                    )
                                );
                            }
                            navigate('/');
                        })
                        .catch((error) => setError(error.message));
                }
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (e: unknown) {
                setError((e as Error).message);
            }
        },
        [user, dispatch, navigate]
    );

    return (
        <div className='login-box set-password'>
            <FormHeader />
            <div className='login-form'>
                <h1>{t('welcome_email', { email })}</h1>
                <p>{t('set_new_password_hint')}</p>
                <form>
                    <div className='password-wrapper'>
                        <label>{t('password')}</label>
                        <input
                            className={`input-form ${
                                error !== undefined ? 'error-form' : ''
                            }`}
                            type={passwordShown ? 'text' : 'password'}
                            id='password'
                            {...register('password')}
                        />
                        <div
                            className='toggle-password'
                            onClick={() => setPasswordShown(!passwordShown)}
                        >
                            <img
                                alt='hide-password'
                                src='/images/ico_hide_password.svg'
                            />
                        </div>
                    </div>
                    <div className='password-wrapper'>
                        <label>{t('repeat_password')}</label>
                        <input
                            className={`input-form ${
                                error !== undefined ? 'error-form' : ''
                            }`}
                            type={repeatPasswordShown ? 'text' : 'password'}
                            id='repeat-password'
                            {...register('repeatPassword')}
                        />
                        <div
                            className='toggle-password'
                            onClick={() =>
                                setRepeatPasswordShown(!repeatPasswordShown)
                            }
                        >
                            <img
                                alt='hide-password'
                                src='/images/ico_hide_password.svg'
                            />
                        </div>
                    </div>
                    {error !== undefined && (
                        <div className='error-toast'>
                            <img alt='error' src='/images/ico_error.svg' />
                            {error}
                        </div>
                    )}
                    <button
                        className='login-button'
                        onClick={handleSubmit(handleSetPassword)}
                    >
                        {t('set_new_password')}
                    </button>
                </form>
            </div>
        </div>
    );
}
